// PD-749 (demodata stream): the demo account list on the organisation and console sign-in pages.
// Owner instruction PD-714: in demo mode the sign-in page lists one account per role with the
// public demo password. The list comes from GET /api/v1/demo/accounts (or the console equivalent),
// which answers 404 outside demo mode — then nothing at all is rendered here.
import { DEMO_ACCOUNTS_PATH, PLATFORM_DEMO_ACCOUNTS_PATH, type DemoAccount, type DemoAccountsResponse } from '@passage/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { get } from '../api.ts';
import { useI18n } from '../i18n.tsx';

const GROUPS = ['role', 'specialist', 'multi', 'platform'] as const;

export function useDemoAccounts(surface: 'organisation' | 'platform') {
  return useQuery({
    queryKey: ['demo-accounts', surface],
    queryFn: () => get<DemoAccountsResponse>(surface === 'platform' ? PLATFORM_DEMO_ACCOUNTS_PATH : DEMO_ACCOUNTS_PATH),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const { t } = useI18n();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 2500);
  };
  return (
    <>
      <button className="button small" type="button" aria-label={label} onClick={() => void copy()}>
        {state === 'copied' ? t('demo.list.copied') : t('demo.list.copy')}
      </button>
      <span className="visually-hidden" role="status" aria-live="polite">
        {state === 'copied' ? t('demo.list.copied') : state === 'failed' ? t('demo.list.copyFailed') : ''}
      </span>
    </>
  );
}

export interface DemoAccountsPanelProps {
  readonly data: DemoAccountsResponse;
  readonly surface: 'organisation' | 'platform';
  /** Fills the sign-in form with the account and submits it; `landing` is opened afterwards. */
  readonly onSignIn: (account: DemoAccount, password: string, landing: string) => void;
  /** Key of the account being signed in, while the request runs. */
  readonly busyKey?: string | null;
}

/** Presentational list (rendered with react-dom/server in tests). */
export function DemoAccountsPanel({ data, surface, onSignIn, busyKey = null }: DemoAccountsPanelProps) {
  const { t, tk, locale, dateTime } = useI18n();
  const text = (b: { en: string; ar: string }) => (locale === 'ar' ? b.ar : b.en);
  const byKey = new Map(data.accounts.map((a) => [a.key, a]));
  return (
    <section className="demo-accounts" aria-labelledby="demo-accounts-title">
      {surface === 'organisation' ? (
        <div className="demo-intro">
          <h2>{t('demo.list.intro.title')}</h2>
          <p>{t('demo.list.intro.body')}</p>
          <p className="note">{t('demo.list.intro.demo')}</p>
        </div>
      ) : null}
      <h2 id="demo-accounts-title">{t('demo.list.title')}</h2>
      <p className="lede">{surface === 'platform' ? t('demo.list.consoleLede') : t('demo.list.lede')}</p>
      <div className="demo-password">
        <span id="demo-password-label">{t('demo.list.password')}</span>
        <code dir="ltr" aria-labelledby="demo-password-label">{data.password}</code>
        <CopyButton value={data.password} label={t('demo.list.copyPassword')} />
        <p className="tiny">{t('demo.list.passwordPublic')}</p>
      </div>
      {GROUPS.map((group) => {
        const accounts = data.accounts.filter((a) => a.group === group);
        if (!accounts.length) return null;
        return (
          <div key={group} className="demo-group">
            <h3>{tk(`demo.list.group.${group}`)}</h3>
            <ul className="demo-cards">
              {accounts.map((a) => {
                const role = text(a.label);
                const busy = busyKey === a.key;
                return (
                  <li key={a.key} className="demo-card">
                    <h4>{role}</h4>
                    <p>
                      <span className="visually-hidden">{t('demo.list.try')}: </span>
                      {text(a.description)}
                    </p>
                    <div className="demo-email">
                      <span className="visually-hidden">{t('demo.list.email')}: </span>
                      <code dir="ltr">{a.email}</code>
                      <CopyButton value={a.email} label={t('demo.list.copyEmail', { role })} />
                    </div>
                    <button className="button dark" type="button" disabled={busyKey !== null} aria-busy={busy || undefined} onClick={() => onSignIn(a, data.password, a.landing)}>
                      {busy ? t('demo.list.signingIn', { role }) : t('demo.list.signInAs', { role })}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {data.links.length ? (
        <div className="demo-group">
          <h3>{t('demo.list.screens')}</h3>
          <p className="tiny">{t('demo.list.screensLede')}</p>
          <ul className="demo-links">
            {data.links.map((l) => {
              const account = l.account ? byKey.get(l.account) : undefined;
              return (
                <li key={l.key}>
                  {account ? (
                    <button className="button quiet" type="button" disabled={busyKey !== null} onClick={() => onSignIn(account, data.password, l.path)}>
                      {text(l.label)} — {t('demo.list.signInAs', { role: text(account.label) })}
                    </button>
                  ) : (
                    <a href={l.path}>{text(l.label)}</a>
                  )}
                  {l.key === 'kiosk' && data.kioskSamples?.length ? (
                    // PD-777 (demosandbox stream): practice visitors to look up at the demo kiosk.
                    <div className="demo-kiosk-samples">
                      <p className="tiny">{t('demo.kiosk.samples')}</p>
                      <ul>
                        {data.kioskSamples.map((k) => (
                          <li key={k.reference} dir="auto">
                            {t('demo.kiosk.sample', { reference: k.reference, check: k.check })}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {data.generatedAt ? <p className="tiny">{t('demo.list.generatedAt', { when: dateTime(data.generatedAt) })}</p> : null}
    </section>
  );
}
